/**
 * <s-review-rotator> cycles a pool of review cards: it shuffles them, shows a
 * set (4 on desktop, 2 below), and swaps to the next set every 7 seconds.
 * Pauses on hover/focus, when the tab is hidden, via the toggle button
 * (command="--toggle"), and never auto-rotates under reduced motion.
 */
class SReviewRotator extends HTMLElement {
  #items = [];
  #order = [];
  #start = 0;
  #timer = 0;
  #hoverPaused = false;
  #userPaused = false;
  #wide = null;
  #reduce = null;
  #list = null;
  #toggle = null;
  #onWideChange = () => this.#render();

  connectedCallback() {
    this.#list = this.querySelector('[data-review-list]');
    this.#items = [...this.querySelectorAll('[data-review]')];
    this.#toggle = this.querySelector('[data-review-toggle]');
    if (!this.#list || this.#items.length < 3) return;

    this.#wide = window.matchMedia('(min-width: 1024px)');
    this.#reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#order = this.#shuffle(this.#items.map((_, index) => index));
    this.#render();

    this.#wide.addEventListener('change', this.#onWideChange);
    this.addEventListener('pointerenter', this.#pauseHover);
    this.addEventListener('pointerleave', this.#resumeHover);
    this.addEventListener('focusin', this.#pauseHover);
    this.addEventListener('focusout', this.#resumeHover);
    this.addEventListener('command', this.#onCommand);

    if (this.#reduce.matches) return;
    if (this.#toggle) this.#toggle.hidden = false;
    this.#timer = window.setInterval(() => this.#advance(), 7000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
    this.#wide?.removeEventListener('change', this.#onWideChange);
    this.removeEventListener('pointerenter', this.#pauseHover);
    this.removeEventListener('pointerleave', this.#resumeHover);
    this.removeEventListener('focusin', this.#pauseHover);
    this.removeEventListener('focusout', this.#resumeHover);
    this.removeEventListener('command', this.#onCommand);
  }

  get #count() {
    return this.#wide?.matches ? 4 : 2;
  }

  #pauseHover = () => {
    this.#hoverPaused = true;
  };

  #resumeHover = () => {
    this.#hoverPaused = false;
  };

  #onCommand = (event) => {
    if (event.command !== '--toggle') return;
    this.#userPaused = !this.#userPaused;
    if (!this.#toggle) return;
    this.#toggle.setAttribute('aria-pressed', String(this.#userPaused));
    this.#toggle.textContent = this.#userPaused ? this.dataset.labelPlay : this.dataset.labelPause;
  };

  #shuffle(values) {
    const result = [...values];
    for (let i = result.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  }

  #render() {
    const visible = new Set();
    for (let offset = 0; offset < this.#count; offset += 1) {
      visible.add(this.#order[(this.#start + offset) % this.#order.length]);
    }
    this.#items.forEach((item, index) => {
      item.hidden = !visible.has(index);
    });
  }

  #advance() {
    if (this.#hoverPaused || this.#userPaused || document.hidden) return;
    this.#start += this.#count;
    if (this.#start >= this.#order.length) {
      this.#start = 0;
      this.#order = this.#shuffle(this.#order);
    }
    this.#list.setAttribute('data-fading', '');
    window.setTimeout(() => {
      this.#render();
      this.#list.removeAttribute('data-fading');
    }, 500);
  }
}

if (!customElements.get('s-review-rotator')) {
  customElements.define('s-review-rotator', SReviewRotator);
}
